import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  IconoAdmin,
  IconoAlertas,
  IconoBandeja,
  IconoCasos,
  IconoConfig,
  IconoEnfoque,
  IconoEnlaces,
  IconoVentas,
  IconoEsperan,
  IconoEstadisticas,
  IconoGuias,
  IconoMas,
  IconoPanel,
  IconoPerfil,
  IconoProveedor,
  IconoSalaCierre,
  IconoSalir,
} from "@/ui/Iconos";
import {
  ApiError,
  api,
  iniciarEnfoque,
  terminarEnfoque,
  type BandejaCount,
  type Notificacion,
  type NotificacionesResumen,
  type PrefsNotif,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ENFOQUE_OPCIONES, ENFOQUE_TEXTOS, formatHoraEnfoque } from "@/lib/notifMeta";
import { activarWebPush } from "@/lib/push";
import ConfirmDialog from "@/ui/ConfirmDialog";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import HelpMenu from "./HelpMenu";

type Rol = string | undefined;

export default function Layout() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const nav = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [bandejaCount, setBandejaCount] = useState<number>(0);
  const [alertasCount, setAlertasCount] = useState<number>(0);
  const [mencionesCount, setMencionesCount] = useState<number>(0);
  const [proveedorCount, setProveedorCount] = useState<number>(0);
  const [enfoqueHasta, setEnfoqueHasta] = useState<string | null>(null);

  const canBandeja = user?.rol === "admin" || user?.rol === "soporte";

  // Refresca el counter de bandeja. Solo admin/soporte tienen acceso.
  const refreshBandejaCount = useCallback(async () => {
    if (!canBandeja) return;
    try {
      const r = await api.get<BandejaCount>("/api/correos/bandeja/count");
      setBandejaCount(r.count);
    } catch {
      /* silencioso, se reintenta proximo tick */
    }
  }, [canBandeja]);

  // Avisos del navegador (popup del SO) para recordatorios disparados.
  const avisadosRef = useRef<Set<number>>(
    (() => {
      try {
        const raw = localStorage.getItem("recordatorios:avisados");
        return new Set<number>(raw ? (JSON.parse(raw) as number[]) : []);
      } catch {
        return new Set<number>();
      }
    })(),
  );
  const primeraAlertasPollRef = useRef(true);

  const dispararAvisosNavegador = useCallback(
    (items: Notificacion[]) => {
      if (
        typeof Notification === "undefined" ||
        Notification.permission !== "granted"
      ) {
        return;
      }
      const avisados = avisadosRef.current;
      const esPrimera = primeraAlertasPollRef.current;
      primeraAlertasPollRef.current = false;
      let cambio = false;
      for (const n of items) {
        if (n.tipo !== "recordatorio" || n.leida_at || avisados.has(n.id)) {
          continue;
        }
        avisados.add(n.id);
        cambio = true;
        if (esPrimera) continue; // no spamear popups por recordatorios viejos
        const notif = new Notification(n.titulo, {
          body: n.cuerpo ?? "",
          tag: `recordatorio-${n.id}`,
        });
        if (n.caso_folio) {
          const folio = n.caso_folio;
          notif.onclick = () => {
            window.focus();
            nav(`/casos/${folio}`);
            notif.close();
          };
        }
      }
      if (cambio) {
        try {
          localStorage.setItem(
            "recordatorios:avisados",
            JSON.stringify([...avisados].slice(-200)),
          );
        } catch {
          /* ignore */
        }
      }
    },
    [nav],
  );

  const refreshAlertas = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get<NotificacionesResumen>("/api/notificaciones");
      setAlertasCount(r.total_no_leidas);
      dispararAvisosNavegador(r.items);
    } catch {
      /* silencioso, se reintenta proximo tick */
    }
  }, [user, dispararAvisosNavegador]);

  // Contador de menciones pendientes ("Esperan mi respuesta").
  const refreshMenciones = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get<{ count: number }>("/api/menciones/count");
      setMencionesCount(r.count);
    } catch {
      /* silencioso, se reintenta proximo tick */
    }
  }, [user]);

  // Tickets con respuesta del proveedor sin contestar. Mismo criterio que las
  // menciones: es un pendiente NUESTRO y el número no se va hasta que alguien le
  // responde al proveedor. Sin esto, la respuesta llegaba a la bitácora y se
  // perdía igual que cuando no se revisaba el portal.
  const refreshProveedor = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get<{ total: number }>("/api/casos/proveedor-pendiente?limite=1");
      setProveedorCount(r.total);
    } catch {
      /* silencioso, se reintenta proximo tick */
    }
  }, [user]);

  useEffect(() => {
    void refreshProveedor();
    const id = setInterval(() => void refreshProveedor(), 60_000);
    const onChange = () => void refreshProveedor();
    window.addEventListener("proveedor:changed", onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("proveedor:changed", onChange);
    };
  }, [refreshProveedor]);

  useEffect(() => {
    void refreshBandejaCount();
    const id = setInterval(() => void refreshBandejaCount(), 60_000);
    const onChange = () => void refreshBandejaCount();
    window.addEventListener("bandeja:changed", onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("bandeja:changed", onChange);
    };
  }, [refreshBandejaCount]);

  useEffect(() => {
    void refreshAlertas();
    const id = setInterval(() => void refreshAlertas(), 60_000);
    const onChange = () => void refreshAlertas();
    window.addEventListener("alertas:changed", onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("alertas:changed", onChange);
    };
  }, [refreshAlertas]);

  useEffect(() => {
    void refreshMenciones();
    const id = setInterval(() => void refreshMenciones(), 60_000);
    const onChange = () => void refreshMenciones();
    window.addEventListener("menciones:changed", onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("menciones:changed", onChange);
    };
  }, [refreshMenciones]);

  // Modo enfoque (Task 12): estado inicial + poll ligero + evento
  // "enfoque:changed" (mismo patrón que bandeja/alertas/menciones) para que
  // el botón se refresque solo si el usuario lo cambió desde el panel de
  // Perfil (NotificacionesPanel también escucha este evento).
  const refreshEnfoque = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get<PrefsNotif>("/api/users/me/notificaciones");
      setEnfoqueHasta(r.enfoque_hasta);
    } catch {
      /* silencioso, se reintenta proximo tick */
    }
  }, [user]);

  useEffect(() => {
    void refreshEnfoque();
    const id = setInterval(() => void refreshEnfoque(), 60_000);
    const onChange = () => void refreshEnfoque();
    window.addEventListener("enfoque:changed", onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("enfoque:changed", onChange);
    };
  }, [refreshEnfoque]);

  useEffect(() => {
    void activarWebPush();
  }, []);

  async function handleLogout() {
    await logout();
    nav("/login", { replace: true });
  }

  /**
   * Acceso de la barra de arriba: ícono con su leyenda chiquita debajo.
   *
   * Mismo patrón que las pestañas del caso y que la barra del celular — un solo
   * vocabulario en toda la app. Antes esta barra tenía texto suelto y, entre
   * 1024 y 1536 px con todos los accesos de admin, no alcanzaba: "Sala de
   * cierre" salía en tres renglones y el nombre, el rol, la ayuda y "Salir" se
   * iban fuera de la pantalla. Se probó dejar solo el ícono en esa banda y no
   * servía: doce estampitas seguidas no dicen qué son. Con la leyenda debajo
   * cabe completa desde 1024 px y además se lee.
   */
  const NavLabel = ({
    Icono,
    children,
  }: {
    Icono: (p: { className?: string }) => JSX.Element;
    children: React.ReactNode;
  }) => (
    <>
      <Icono className="h-[18px] w-[18px]" />
      <span className="text-[9px] leading-none">{children}</span>
    </>
  );

  // Sin tooltips (regla de Joe): qué hace cada destino lo explica la Ayuda de
  // su propia página. Los accesos llevan su leyenda visible; los que no la
  // tienen (el nombre que lleva a Mi perfil) llevan `aria-label`.
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center gap-1 whitespace-nowrap text-sm transition ${isActive ? "text-accent-400" : "text-zinc-400 hover:text-white"}`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="ark-barra-arriba border-b border-ink-700 bg-ink-900/80 backdrop-blur sticky top-0 z-30">
        <div className="w-full lg:w-4/5 max-w-[1800px] mx-auto px-4 h-14 flex items-center justify-between gap-2">
          <NavLink to="/" className="flex items-center gap-2 min-w-0">
            <img
              src="/emprenet-icon.png"
              alt="Emprenet"
              className="h-7 w-7 rounded-full ring-1 ring-ink-700 shrink-0"
            />
            <span className="font-mono text-accent-400 font-semibold truncate">ArkTickets</span>
          </NavLink>

          {/* Navegación de escritorio (md+) */}
          <nav className="hidden lg:flex items-center gap-3">
            <NavLink to="/" className={linkClass} end aria-label="Panel">
              <NavLabel Icono={IconoPanel}>Panel</NavLabel>
            </NavLink>
            <NavLink to="/estadisticas" className={linkClass} aria-label="Estadísticas">
              <NavLabel Icono={IconoEstadisticas}>Estadísticas</NavLabel>
            </NavLink>
            <NavLink to="/casos" className={linkClass} aria-label="Casos">
              <NavLabel Icono={IconoCasos}>Casos</NavLabel>
            </NavLink>
            {canBandeja && (
              <NavLink to="/bandeja" className={linkClass}>
                <span className="relative flex flex-col items-center gap-1">
                  <NavLabel Icono={IconoBandeja}>Bandeja</NavLabel>
                  {bandejaCount > 0 && (
                    <span className="absolute -right-3 -top-1 min-w-[16px] rounded-full bg-accent-500 px-1 text-[9px] font-mono leading-4 text-center text-ink-950">
                      {bandejaCount}
                    </span>
                  )}
                </span>
              </NavLink>
            )}
            <NavLink to="/notificaciones" className={linkClass}>
              <span className="relative flex flex-col items-center gap-1">
                <NavLabel Icono={IconoAlertas}>Alertas</NavLabel>
                {alertasCount > 0 && (
                  <span className="absolute -right-3 -top-1 min-w-[16px] rounded-full bg-accent-500 px-1 text-[9px] font-mono leading-4 text-center text-ink-950">
                    {alertasCount}
                  </span>
                )}
              </span>
            </NavLink>
            <EnfoqueBoton enfoqueHasta={enfoqueHasta} onUpdate={setEnfoqueHasta} />
            <NavLink to="/menciones" className={linkClass}>
              <span className="relative flex flex-col items-center gap-1">
                <NavLabel Icono={IconoEsperan}>Esperan</NavLabel>
                {mencionesCount > 0 && (
                  <span className="absolute -right-3 -top-1 min-w-[16px] rounded-full bg-accent-500 px-1 text-[9px] font-mono leading-4 text-center text-ink-950">
                    {mencionesCount}
                  </span>
                )}
              </span>
            </NavLink>
            <NavLink to="/proveedor" className={linkClass} aria-label="El proveedor contestó">
              <span className="relative flex flex-col items-center gap-1">
                <NavLabel Icono={IconoProveedor}>Proveedor</NavLabel>
                {proveedorCount > 0 && (
                  <span className="absolute -right-3 -top-1 min-w-[16px] rounded-full bg-accent-500 px-1 text-[9px] font-mono leading-4 text-center text-ink-950">
                    {proveedorCount}
                  </span>
                )}
              </span>
            </NavLink>
            <NavLink to="/guias" className={linkClass} aria-label="Guías">
              <NavLabel Icono={IconoGuias}>Guías</NavLabel>
            </NavLink>
            <NavLink to="/shortlinks" className={linkClass} aria-label="Enlaces">
              <NavLabel Icono={IconoEnlaces}>Enlaces</NavLabel>
            </NavLink>
            <NavLink to="/ventas" className={linkClass} aria-label="Ventas">
              <NavLabel Icono={IconoVentas}>Ventas</NavLabel>
            </NavLink>
            {user?.rol === "admin" && (
              <>
                <NavLink to="/admin" className={linkClass} aria-label="Admin">
                  <NavLabel Icono={IconoAdmin}>Admin</NavLabel>
                </NavLink>
                <NavLink to="/configuracion" className={linkClass} aria-label="Configuración">
                  <NavLabel Icono={IconoConfig}>Config</NavLabel>
                </NavLink>
              </>
            )}
            {user?.es_dueno && (
              <NavLink to="/sala-cierre" className={linkClass} aria-label="Sala de cierre">
                <NavLabel Icono={IconoSalaCierre}>Sala de cierre</NavLabel>
              </NavLink>
            )}
            <span className="text-zinc-600">·</span>
            <NavLink to="/perfil" className={linkClass} aria-label={`Mi perfil (${user?.nombre ?? ""})`}>
              <span className="text-sm truncate max-w-[10rem] xl:max-w-none">{user?.nombre}</span>
            </NavLink>
            <span className="hidden xl:inline text-[10px] text-zinc-500 uppercase tracking-wide">
              {user?.rol}
            </span>
            <HelpMenu />
            <button
              onClick={() => setConfirmOpen(true)}
              aria-label="Cerrar sesión"
              className="flex flex-col items-center justify-center gap-1 text-zinc-400 transition hover:text-white"
            >
              <NavLabel Icono={IconoSalir}>Salir</NavLabel>
            </button>
          </nav>

          {/* Lado derecho en mobile: enfoque + ayuda (el resto vive en la barra inferior) */}
          <div className="flex items-center gap-1 lg:hidden">
            <EnfoqueBoton enfoqueHasta={enfoqueHasta} onUpdate={setEnfoqueHasta} />
            <HelpMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 w-full lg:w-4/5 max-w-[1800px] mx-auto px-4 py-6 pb-24 lg:pb-6">
        <Outlet />
      </main>

      {/* Barra inferior (solo mobile) */}
      <BottomNav
        bandejaCount={bandejaCount}
        alertasCount={alertasCount}
        mencionesCount={mencionesCount}
        canBandeja={canBandeja}
        onMore={() => setMoreOpen(true)}
      />

      {/* Sheet "Más" con el resto de destinos */}
      <MoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        rol={user?.rol}
        esDueno={user?.es_dueno}
        nombre={user?.nombre}
        mencionesCount={mencionesCount}
        proveedorCount={proveedorCount}
        onSalir={() => {
          setMoreOpen(false);
          setConfirmOpen(true);
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Cerrar sesión"
        description="Tu sesión se cerrará en este navegador. Necesitarás volver a iniciar sesión."
        confirmLabel="Cerrar sesión"
        cancelLabel="Quedarme"
        onConfirm={handleLogout}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Barra de navegación inferior (mobile). Destinos primarios al alcance del
// pulgar; el resto va en el sheet "Más".
// ----------------------------------------------------------------------------
function BottomNav({
  bandejaCount,
  alertasCount,
  mencionesCount,
  canBandeja,
  onMore,
}: {
  bandejaCount: number;
  alertasCount: number;
  mencionesCount: number;
  canBandeja: boolean;
  onMore: () => void;
}) {
  const itemCls = ({ isActive }: { isActive: boolean }) =>
    `relative flex flex-1 min-w-0 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] ${
      isActive ? "text-accent-400" : "text-zinc-400"
    }`;
  return (
    <nav className="ark-barra-abajo lg:hidden fixed inset-x-0 bottom-0 z-30 border-t border-ink-700 bg-ink-900/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch">
        <NavLink to="/" end className={itemCls}>
          <IconoPanel className="h-[22px] w-[22px]" />
          <span>Inicio</span>
        </NavLink>
        <NavLink to="/casos" className={itemCls}>
          <IconoCasos className="h-[22px] w-[22px]" />
          <span>Casos</span>
        </NavLink>
        {canBandeja && (
          <NavLink to="/bandeja" className={itemCls}>
            <span className="relative">
              <IconoBandeja className="h-[22px] w-[22px]" />
              <NavBadge n={bandejaCount} />
            </span>
            <span>Bandeja</span>
          </NavLink>
        )}
        <NavLink to="/notificaciones" className={itemCls}>
          <span className="relative">
            <IconoAlertas className="h-[22px] w-[22px]" />
            <NavBadge n={alertasCount} />
          </span>
          <span>Alertas</span>
        </NavLink>
        <button type="button" onClick={onMore} className={itemCls({ isActive: false })}>
          <span className="relative">
            <IconoMas className="h-[22px] w-[22px]" />
            <NavBadge n={mencionesCount} />
          </span>
          <span>Más</span>
        </button>
      </div>
    </nav>
  );
}

function NavBadge({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="absolute -right-2 -top-1 min-w-[16px] rounded-full bg-accent-500 px-1 text-[9px] font-mono leading-[16px] text-ink-950">
      {n > 99 ? "99+" : n}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Sheet "Más": destinos secundarios + sesión. Reusa el Modal (que en mobile
// es un bottom-sheet).
// ----------------------------------------------------------------------------
function MoreSheet({
  open,
  onOpenChange,
  rol,
  esDueno,
  nombre,
  mencionesCount,
  proveedorCount,
  onSalir,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  rol: Rol;
  esDueno: boolean | undefined;
  nombre: string | undefined;
  mencionesCount: number;
  proveedorCount: number;
  onSalir: () => void;
}) {
  const close = () => onOpenChange(false);
  const rowCls = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-3 text-base ${
      isActive ? "bg-ink-800 text-accent-300" : "text-zinc-200 hover:bg-ink-800"
    }`;
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Más" closeOnOutsideClick>
      <div className="flex flex-col gap-1">
        <NavLink to="/menciones" onClick={close} className={rowCls}>
          <IconoEsperan className="h-5 w-5 shrink-0" />
          <span className="flex-1">Esperan mi respuesta</span>
          {/* En línea, como el de «El proveedor contestó». Era `absolute` sin
              padre `relative` y se pintaba en la esquina de la hoja, no en su
              renglón (2026-10-13). */}
          {mencionesCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 text-[11px] font-mono leading-5 text-ink-950">
              {mencionesCount}
            </span>
          )}
        </NavLink>
        <NavLink to="/proveedor" onClick={close} className={rowCls}>
          <IconoProveedor className="h-5 w-5 shrink-0" />
          <span className="flex-1">El proveedor contestó</span>
          {proveedorCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 text-[11px] font-mono leading-5 text-ink-950">
              {proveedorCount}
            </span>
          )}
        </NavLink>
        <NavLink to="/estadisticas" onClick={close} className={rowCls}>
          <IconoEstadisticas className="h-5 w-5 shrink-0" /> Estadísticas
        </NavLink>
        {esDueno && (
          <NavLink to="/sala-cierre" onClick={close} className={rowCls}>
            <IconoSalaCierre className="h-5 w-5 shrink-0" /> Sala de cierre
          </NavLink>
        )}
        <NavLink to="/guias" onClick={close} className={rowCls}>
          <IconoGuias className="h-5 w-5 shrink-0" /> Guías
        </NavLink>
        <NavLink to="/shortlinks" onClick={close} className={rowCls}>
          <IconoEnlaces className="h-5 w-5 shrink-0" /> Enlaces
        </NavLink>
        <NavLink to="/ventas" onClick={close} className={rowCls}>
          <IconoVentas className="h-5 w-5 shrink-0" /> Ventas
        </NavLink>
        {rol === "admin" && (
          <>
            <NavLink to="/admin" onClick={close} className={rowCls}>
              <IconoAdmin className="h-5 w-5 shrink-0" /> Admin
            </NavLink>
            <NavLink to="/configuracion" onClick={close} className={rowCls}>
              <IconoConfig className="h-5 w-5 shrink-0" /> Config
            </NavLink>
          </>
        )}
        <div className="my-1 h-px bg-ink-700" />
        <NavLink to="/perfil" onClick={close} className={rowCls}>
          <IconoPerfil className="h-5 w-5 shrink-0" />
          <span className="flex flex-col">
            <span>{nombre ?? "Mi perfil"}</span>
            <span className="text-xs uppercase tracking-wide text-zinc-500">{rol}</span>
          </span>
        </NavLink>
        <button
          type="button"
          onClick={onSalir}
          className="flex items-center gap-3 rounded-lg px-3 py-3 text-base text-red-300 hover:bg-red-500/10"
        >
          <IconoSalir className="h-5 w-5 shrink-0" /> Cerrar sesión
        </button>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Botón "Modo enfoque" (🎯): silencia navegador/Telegram un rato (la campanita
// sigue recibiendo todo, ver `services/notify.py`). UN solo componente usado
// dos veces (nav de escritorio, junto a Alertas, y encabezado mobile, junto a
// Ayuda) para no duplicar lógica ni tocar la barra inferior — Alertas y
// Menciones viven ahí y no se desacomodan. `NotificacionesPanel.tsx` (Perfil)
// trae la misma acción y ambos se sincronizan vía el evento "enfoque:changed".
// ----------------------------------------------------------------------------
function EnfoqueBoton({
  enfoqueHasta,
  onUpdate,
}: {
  enfoqueHasta: string | null;
  onUpdate: (hasta: string | null) => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [cargando, setCargando] = useState(false);
  const activo = !!enfoqueHasta && new Date(enfoqueHasta) > new Date();

  async function iniciar(minutos: number) {
    setCargando(true);
    try {
      const prefs = await iniciarEnfoque(minutos);
      onUpdate(prefs.enfoque_hasta);
      window.dispatchEvent(new Event("enfoque:changed"));
      toast.success(
        "Modo enfoque activado",
        prefs.enfoque_hasta
          ? `Hasta las ${formatHoraEnfoque(prefs.enfoque_hasta)}`
          : undefined,
      );
      setOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo activar el modo enfoque", msg);
    } finally {
      setCargando(false);
    }
  }

  async function terminar() {
    setCargando(true);
    try {
      const prefs = await terminarEnfoque();
      onUpdate(prefs.enfoque_hasta);
      window.dispatchEvent(new Event("enfoque:changed"));
      toast.success("Modo enfoque terminado");
      setOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo terminar el modo enfoque", msg);
    } finally {
      setCargando(false);
    }
  }

  return (
    <Dropdown.Root open={open} onOpenChange={setOpen}>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          // Activo, el botón enseña la hora de fin a la vista (antes solo el
          // tooltip decía «Enfoque activo hasta las …»).
          aria-label={
            activo && enfoqueHasta
              ? `Modo enfoque, activo hasta las ${formatHoraEnfoque(enfoqueHasta)}`
              : "Modo enfoque"
          }
          className={`inline-flex h-8 md:h-7 items-center gap-1 rounded-full border px-2 text-xs shrink-0
                     focus:outline-none focus:ring-2 focus:ring-accent-500 ${
                       activo
                         ? "border-accent-500/60 bg-accent-500/10 text-accent-300"
                         : "border-ink-600 bg-ink-800 text-zinc-300 hover:text-accent-400 hover:border-accent-500"
                     }`}
        >
          <IconoEnfoque className="h-4 w-4 shrink-0" />
          {activo && enfoqueHasta && (
            <span className="font-mono">{formatHoraEnfoque(enfoqueHasta)}</span>
          )}
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={8}
          className="z-50 w-64 max-w-[calc(100vw-1.5rem)] rounded-lg border border-ink-700 bg-ink-900 p-3 shadow-2xl
                     data-[state=open]:animate-in data-[state=closed]:animate-out
                     data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          {activo && enfoqueHasta ? (
            <div className="space-y-2">
              <p className="text-sm text-zinc-200">
                🎯 Enfoque hasta las{" "}
                <span className="font-mono">{formatHoraEnfoque(enfoqueHasta)}</span>
              </p>
              <p className="text-[11px] text-zinc-500 leading-snug">{ENFOQUE_TEXTOS.detalle}</p>
              <button
                type="button"
                onClick={() => void terminar()}
                disabled={cargando}
                className="btn-ghost text-sm w-full disabled:opacity-50"
              >
                Terminar
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-zinc-400">{ENFOQUE_TEXTOS.pregunta}</p>
              <div className="grid grid-cols-3 gap-1.5">
                {ENFOQUE_OPCIONES.map((o) => (
                  <button
                    key={o.minutos}
                    type="button"
                    onClick={() => void iniciar(o.minutos)}
                    disabled={cargando}
                    className="rounded border border-ink-700 px-2 py-1.5 text-sm text-zinc-200
                               hover:bg-ink-800 hover:text-white disabled:opacity-50"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 leading-snug">{ENFOQUE_TEXTOS.detalle}</p>
            </div>
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

